if (!customElements.get('sticky-purchase-bar')) {
  class StickyPurchaseBar extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
    }

    connectedCallback() {
      this.originalSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.stickyButton = this.querySelector('.sticky-purchase-bar__add-btn');
      this.stickyPrice = this.querySelector('.sticky-purchase-bar__price');
      this.stickyImage = this.querySelector('.sticky-purchase-bar__image img');

      if (!this.originalSubmitButton) return;

      this.mobileQuery = window.matchMedia('(max-width: 989px)');

      this.initIntersectionObserver();
      this.initHeaderObserver();
      this.initAddToCart();
      this.initVariantSync();
    }

    disconnectedCallback() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.loadingObserver) this.loadingObserver.disconnect();
      document.removeEventListener('variant:change', this.boundVariantChange);
      if (this.boundResize) window.removeEventListener('resize', this.boundResize);
    }

    initIntersectionObserver() {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.updateBarPosition(entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      this.intersectionObserver.observe(this.originalSubmitButton);

      // Handle orientation changes and viewport resize
      this.boundResize = () => this.updateBarPosition();
      window.addEventListener('resize', this.boundResize, { passive: true });
    }

    updateBarPosition(isIntersecting) {
      const rect = this.originalSubmitButton.getBoundingClientRect();

      // If called without argument (resize), compute intersection manually
      if (typeof isIntersecting === 'undefined') {
        isIntersecting = rect.top < window.innerHeight && rect.bottom > 0;
      }

      // Show bar when button is scrolled past (above viewport)
      if (!isIntersecting && rect.top < 0) {
        this.classList.add('is-visible');
      } else {
        this.classList.remove('is-visible');
      }
    }

    initHeaderObserver() {
      const headerSection = document.querySelector('.shopify-section-header');
      if (!headerSection) return;

      const updateHeaderState = () => {
        const isSticky = headerSection.classList.contains('shopify-section-header-sticky');
        const isHidden = headerSection.classList.contains('shopify-section-header-hidden');
        this.dataset.headerVisible = isSticky && !isHidden ? 'true' : 'false';
      };

      updateHeaderState();

      this.headerObserver = new MutationObserver(updateHeaderState);
      this.headerObserver.observe(headerSection, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    initAddToCart() {
      this.stickyButton.addEventListener('click', () => {
        if (this.originalSubmitButton && !this.originalSubmitButton.disabled) {
          this.originalSubmitButton.click();
        }
      });

      // Mirror loading state from original button
      this.loadingObserver = new MutationObserver(() => {
        this.stickyButton.classList.toggle(
          'loading',
          this.originalSubmitButton.classList.contains('loading')
        );
      });
      this.loadingObserver.observe(this.originalSubmitButton, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    initVariantSync() {
      // Listen for variant changes to update availability
      this.boundVariantChange = this.onVariantChange.bind(this);
      document.addEventListener('variant:change', this.boundVariantChange);

      // Watch the price element for async innerHTML updates from renderProductInfo()
      const priceEl = document.getElementById(`price-${this.sectionId}`);
      if (priceEl) {
        this.priceObserver = new MutationObserver(() => {
          this.syncPrice(priceEl);
        });
        this.priceObserver.observe(priceEl, { childList: true, subtree: true });
      }
    }

    onVariantChange(event) {
      const { variant, sectionId } = event.detail;
      if (sectionId !== this.sectionId) return;

      // Update button availability
      if (variant && variant.available) {
        this.stickyButton.disabled = false;
        this.stickyButton.querySelector('.button-label').textContent =
          this.originalSubmitButton.querySelector('.button-label').textContent;
      } else {
        this.stickyButton.disabled = true;
        this.stickyButton.querySelector('.button-label').textContent =
          this.originalSubmitButton.querySelector('.button-label').textContent;
      }

      // Update image if variant has a featured image
      if (variant && variant.featured_image && this.stickyImage) {
        const newSrc = variant.featured_image.src.replace(/(\.[^.]+)$/, '_120x$1');
        this.stickyImage.src = newSrc;
        this.stickyImage.srcset = '';
      }
    }

    syncPrice(priceEl) {
      if (this.stickyPrice) {
        this.stickyPrice.innerHTML = priceEl.innerHTML;
      }
    }
  }

  customElements.define('sticky-purchase-bar', StickyPurchaseBar);
}
